import React from 'react'
import { FormattedMessage, injectIntl } from 'react-intl'
import type { IntlShape } from 'react-intl'
import { NavLink } from 'react-router-dom'
import { media } from 'app/assets/styles'
import { colors as oldColors } from 'shared/assets/styles'
import { colors } from 'app-v2/design-systems/theme/colors'
import { H2, H5 } from 'app/common/typography'
import styled from 'styled-components'
import { Chevron } from 'app/assets/icons'
import { Icon } from 'app-v2/shared/features/icon'
import type { EventMetadata } from 'shared/store/metadata'
import { eventsDefinitions } from 'shared/store/metadata'
import type { MenuListItem } from './types'
import { COLOR } from 'app-v2/design-tokens'
import { useSelector } from 'react-redux'
import type { RootState } from 'shared/store/reducers'
import { featureCodes } from 'shared/utils'

const { black40, lightGrey, red, red2, text } = oldColors

type Props = {
    categories: Array<MenuListItem>,
    hideMobileMenu: boolean,
    hideDesktopMenu: boolean,
    sendMeta?: EventMetadata => void,
    intl: IntlShape,
    propRoute: string,
    menuBackgroundImage?: boolean,
    menuTitle?: string
}

const Menu = injectIntl((props: Props) => {
    const {
        categories,
        hideMobileMenu,
        hideDesktopMenu,
        sendMeta,
        intl,
        propRoute,
        menuBackgroundImage = false,
        menuTitle
    } = props

    const isActiveZendesk = useSelector((state: RootState) =>
        state.featuresState.available.includes(
            featureCodes.ACTIVATION_ZENDESK_REDIRECTION
        )
    )

    return (
        <MenuContainer
            hideMobileMenu={hideMobileMenu}
            hideDesktopMenu={hideDesktopMenu}
            menuBackgroundImage={menuBackgroundImage}>
            {menuTitle && (
                <StyledH2>
                    <FormattedMessage id={menuTitle} />
                </StyledH2>
            )}
            {categories.map((category: MenuListItem, index) => {
                const { categoryId, routeId, title } = category
                const isFaqIcon = categoryId === 'QUESTION'
                const cardRoute = intl.formatMessage({
                    id: propRoute
                })
                const handleClick = (e: Event) => {
                    if (isFaqIcon && !!isActiveZendesk) {
                        e.preventDefault()
                        window.location.href = process.env.REACT_APP_ZENDESK_URL
                        return
                    }

                    const meta = {
                        ...eventsDefinitions['CATEGORY_MENU_CLICK'],
                        event_action: title
                    }
                    if (sendMeta) sendMeta(meta)
                }
                return (
                    <StyledNavLink
                        exact
                        to={
                            routeId
                                ? isFaqIcon
                                    ? routeId
                                    : `${cardRoute}/${routeId}`
                                : cardRoute
                        }
                        activeClassName="active"
                        key={index}
                        menubackgroundimage={menuBackgroundImage.toString()}
                        onClick={handleClick}>
                        <FakeColumn />
                        <Column>
                            <Item>
                                <IconContainer isFaqIcon={isFaqIcon}>
                                    <Icon
                                        code={categoryId}
                                        color={
                                            isFaqIcon
                                                ? colors.darkBrown
                                                : COLOR.SECONDARY
                                        }
                                        background={COLOR.SECONDARY}
                                        size={24}
                                    />
                                </IconContainer>
                                <StyledH5 key={index}>{title}</StyledH5>
                            </Item>
                            <StyledChevron color={text} />
                        </Column>
                    </StyledNavLink>
                )
            })}
        </MenuContainer>
    )
})

const Column = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;

    ${media.large`
        width: 200px;
    `};
`

const StyledNavLink = styled(NavLink)`
    display: flex;
    align-items: center;
    border-top: 0.5px solid ${lightGrey};
    box-sizing: border-box;
    width: 100%;
    height: 55px;
    padding: 15px 0;
    cursor: pointer;
    white-space: nowrap;

    ${media.medium`
        border: none;
        &.active, &:hover {
            background-color: ${props =>
                props.menubackgroundimage === 'true'
                    ? `${colors.darkBrown}`
                    : `${red2}`};
        }
    `};
`

const FakeColumn = styled.div`
    display: none;

    ${media.medium`
        display: block;
        width: 20px;
    `};

    ${media.large`
        width: calc((100vw - 1280px) / 2);
    `};
`
const MenuContainer = styled.div`
    display: ${props => (props.hideMobileMenu ? 'none' : 'flex')};
    justify-content: center;
    align-items: flex-start;
    flex-direction: column;

    ${media.medium`
        justify-content: flex-start;
        display: ${props => (props.hideDesktopMenu ? 'none' : 'flex')};
        border: none;
        margin-left: -20px;
        ${props =>
            props.menuBackgroundImage
                ? `background-color: ${COLOR.PRIMARY}`
                : `background-color: ${red}`};
        background-repeat: no-repeat;
        background-size: cover;
        width: 22.5%;
    `};

    ${media.large`
        width: calc(200px + (100vw - 1280px) / 2);
        margin-left: 0;
    `};
`

const StyledH5 = styled(H5)`
    margin-left: 10px;
    ${media.medium`
        color: ${COLOR.SECONDARY};
        font-size: 16px;
    `};
`

const StyledH2 = styled(H2)`
    margin: 40px 0 15px 0;
    ${media.medium`
        display: none;
    `};
`

const StyledChevron = styled(Chevron)`
    float: right;
    ${media.medium`
       display: none;
    `};
`

const Item = styled.div`
    display: flex;
`

const IconContainer = styled.div`
    ${media.medium`
        svg {
            fill: white;
        }
        ${props =>
            props.isFaqIcon &&
            `svg {
                g {
                    path#background {
                        fill: white;
                    }
                    path#color {
                        fill: ${text};
                    }
                }
            }`}
    `};
    display: flex;
    align-items: center;
`

export { Menu }
